import { v4 as uuid } from 'uuid';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { citations, reactions } from '@Constants';
import searchStorage from '@Services/SearchStorage';
import { SearchTypes } from '@Constants/CommonConstants';
import Breadcrumb from '@Services/Breadcrumb';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { newToastMessage } from '@Containers/ToastMessages/ToastMessagesActions';
import {
  trackCopyToClipboard,
  trackGenAIContentUpdated,
  trackSurveySubmissionForSummary,
  trackSummaryGenerationCancelled,
} from '@Containers/Summary/analytics';
import { replaceRefsInSummaryWithIndexes } from '@Containers/Summary/summaryRefsUtil';
import formatReferencesCopyToClipboardUtil from '@Containers/Summary/formatReferencesCopyToClipboardUtil';
import {
  SUMMARY_STATUS,
  SUMMARY_SCOPE,
  SUMMARY_POLLING_INTERVAL,
  SUMMARY_POLLING_MAX_ATTEMPTS,
  SUMMARY_POLLING_MAX_DURATION,
  SUMMARY_POLLING_STATUS,
  SUMMARY_ENTRY_START,
  SUMMARY_ENTRY_ACCEPTED,
  SUMMARY_ENTRY_SUCCESS,
  SUMMARY_ENTRY_ERROR,
  SUMMARY_ENTRY_CANCELLED,
  SUMMARY_ENTRY_FEEDBACK,
  SUMMARY_ENTRY_ADD_VALID_RESULT_NAME,
  RESET_SUMMARY_STATE,
  isSummarySelectionValid,
} from './constants';

// AR-1.5: one poll handle per chain, cleared only for that chain — starting a generation on
// chain B must never kill chain A's in-flight poll. Also doubles as "which entryId is the
// active request for this chain", so a late response for a superseded/cancelled entry is a no-op.
const activeRequestByChain = {};
// Polls paused while the tab is hidden, resumed on visibility (AR-1.5 poll policy).
const pausedPollsByChain = new Map();
let visibilityChangeHandler = null;

function isEntryStillActive(chainKey, entryId) {
  return activeRequestByChain[chainKey]?.entryId === entryId;
}

function removeVisibilityChangeHandler() {
  if (!visibilityChangeHandler || typeof document === 'undefined') return;

  document.removeEventListener('visibilitychange', visibilityChangeHandler);
  visibilityChangeHandler = null;
}

function resumePausedPolls(dispatch) {
  removeVisibilityChangeHandler();
  const pausedPolls = [...pausedPollsByChain.entries()];
  pausedPollsByChain.clear();

  pausedPolls.forEach(([chainKey, { entryId, summaryId, attempt }]) => {
    if (isEntryStillActive(chainKey, entryId)) {
      // eslint-disable-next-line no-use-before-define
      dispatch(pollSummaryStatus({ chainKey, entryId, summaryId, attempt }));
    }
  });
}

function ensureVisibilityChangeHandler(dispatch) {
  if (visibilityChangeHandler || typeof document === 'undefined') return;

  visibilityChangeHandler = () => {
    if (!document.hidden) {
      resumePausedPolls(dispatch);
    }
  };
  document.addEventListener('visibilitychange', visibilityChangeHandler);
}

function clearChainPolling(chainKey) {
  const active = activeRequestByChain[chainKey];
  if (active?.pollTimeoutId) {
    clearTimeout(active.pollTimeoutId);
  }
  pausedPollsByChain.delete(chainKey);
  if (!pausedPollsByChain.size) {
    removeVisibilityChangeHandler();
  }
}

function clearActiveRequest(chainKey, entryId) {
  if (activeRequestByChain[chainKey]?.entryId === entryId) {
    clearChainPolling(chainKey);
    delete activeRequestByChain[chainKey];
  }
}

function dispatchEntryError(dispatch, { chainKey, entryId, summaryId, error }) {
  dispatch({ type: SUMMARY_ENTRY_ERROR, payload: { chainKey, entryId, summaryId, error } });
}

function dispatchEntrySuccess(dispatch, { chainKey, entryId, summaryId, summaryResponse }) {
  const activeRequest = activeRequestByChain[chainKey];
  dispatch({
    type: SUMMARY_ENTRY_SUCCESS,
    payload: { chainKey, entryId, summaryId, summaryResponse },
  });
  trackGenAIContentUpdated({
    summaryResponse,
    activeBreadcrumb: activeRequest?.trackingBreadcrumb,
    context: activeRequest?.context,
    processingTime: activeRequest?.startedAt === undefined ? undefined : Date.now() - activeRequest.startedAt,
  });
}

function isCompletedSummaryResponse(data) {
  return data?.status === SUMMARY_POLLING_STATUS.COMPLETED || Boolean(data?.summary);
}

function hasPollingTimedOut(chainKey, attempt) {
  const startedAt = activeRequestByChain[chainKey]?.startedAt;
  const durationExceeded = startedAt !== undefined && Date.now() - startedAt >= SUMMARY_POLLING_MAX_DURATION;
  return attempt > SUMMARY_POLLING_MAX_ATTEMPTS || durationExceeded;
}

function pollSummaryStatus({ chainKey, entryId, summaryId, attempt }) {
  return async (dispatch) => {
    if (!isEntryStillActive(chainKey, entryId)) return;

    if (typeof document !== 'undefined' && document.hidden) {
      pausedPollsByChain.set(chainKey, { entryId, summaryId, attempt });
      ensureVisibilityChangeHandler(dispatch);
      return;
    }

    if (hasPollingTimedOut(chainKey, attempt)) {
      dispatchEntryError(dispatch, {
        chainKey,
        entryId,
        summaryId,
        error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: 'Summary generation timed out.' },
      });
      clearActiveRequest(chainKey, entryId);
      return;
    }

    try {
      const { status, data } = await MessageBroker.send(RequestUrls.aiSearchSummaryStatusById, { summaryId });

      if (!isEntryStillActive(chainKey, entryId)) return;

      if (status === 404 || data?.status === SUMMARY_POLLING_STATUS.ERROR) {
        dispatchEntryError(dispatch, { chainKey, entryId, summaryId, error: data?.code ? data : { code: 'SUMMARY_NOT_FOUND' } });
        clearActiveRequest(chainKey, entryId);
        return;
      }

      if (isCompletedSummaryResponse(data)) {
        if (data?.summaryId && data.summaryId !== summaryId) {
          dispatchEntryError(dispatch, {
            chainKey,
            entryId,
            summaryId,
            error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: 'Summary response identity did not match the request.' },
          });
          clearActiveRequest(chainKey, entryId);
          return;
        }
        dispatchEntrySuccess(dispatch, { chainKey, entryId, summaryId, summaryResponse: data });
        clearActiveRequest(chainKey, entryId);
        return;
      }

      const timeoutId = setTimeout(() => {
        dispatch(pollSummaryStatus({ chainKey, entryId, summaryId, attempt: attempt + 1 }));
      }, SUMMARY_POLLING_INTERVAL);
      activeRequestByChain[chainKey] = { ...activeRequestByChain[chainKey], entryId, pollTimeoutId: timeoutId };
    } catch (error) {
      if (!isEntryStillActive(chainKey, entryId)) return;
      dispatchEntryError(dispatch, {
        chainKey,
        entryId,
        summaryId,
        error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: error?.message },
      });
      clearActiveRequest(chainKey, entryId);
      Logger.error('Error while polling summary status:', error);
    }
  };
}

/**
 * SD-4 item 1: POST /api/semantic/summary. Replaces the old GET-based `fetchSummary`.
 * @param {string} chainKey - composite chain key (AR-1.4), used as the state key and to scope polling.
 * @param {string} resultName - the ACTIVE resultName at generation time.
 * @param {string} databaseId
 * @param {'citations'|'reactions'} context
 * @param {'TOP'|'SELECTED'} scope
 * @param {string} [sort]
 * @param {string} [order]
 * @param {string} query
 * @param {string} [semanticResultName] - only for citations context, WEIGHT desc sort.
 * @param {{index: string, id?: string}[]} [selectedItems] - caller maps the screen's own selection
 *   model to this shape (AR-1.6) — this module does not read Results-page selection state directly.
 */
export function generateSummary({
  chainKey,
  resultName,
  databaseId,
  context,
  scope,
  sort,
  order,
  query,
  semanticResultName,
  selectedItems = [],
  pageNumber,
  trackingBreadcrumb,
}) {
  return async (dispatch) => {
    if (scope === SUMMARY_SCOPE.SELECTED && !isSummarySelectionValid(selectedItems)) {
      return;
    }

    const entryId = uuid();
    clearChainPolling(chainKey);

    dispatch({
      type: SUMMARY_ENTRY_START,
      payload: {
        chainKey,
        lastViewedAt: Date.now(),
        attempt: {
          id: entryId,
          summaryId: null,
          status: SUMMARY_STATUS.LOADING,
          scope,
          sourceResultParams: { resultName, sort, order, pageNumber },
          error: null,
          cancelled: false,
        },
      },
    });

    const body = {
      resultName,
      databaseId,
      context,
      query,
      scope,
      sort,
      order,
      ...(context === citations && sort === 'WEIGHT' && order === 'desc' && semanticResultName ? { semanticResultName } : {}),
      selectedItems: scope === SUMMARY_SCOPE.SELECTED ? selectedItems : [],
    };

    let pendingRequest;
    try {
      pendingRequest = MessageBroker.send(RequestUrls.aiSearchSummary, null, null, body);
      activeRequestByChain[chainKey] = {
        entryId,
        startedAt: Date.now(),
        context,
        trackingBreadcrumb,
        pendingRequest,
        pollTimeoutId: null,
      };

      const { status, data } = await pendingRequest;

      if (!isEntryStillActive(chainKey, entryId)) return; // cancelled/superseded mid-flight

      if (status >= 400) {
        dispatchEntryError(dispatch, { chainKey, entryId, summaryId: null, error: data });
        clearActiveRequest(chainKey, entryId);
        return;
      }

      if (isCompletedSummaryResponse(data)) {
        const summaryId = data?.summaryId;
        if (!summaryId) {
          dispatchEntryError(dispatch, {
            chainKey,
            entryId,
            summaryId: null,
            error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: 'Completed summary response did not include a summaryId.' },
          });
          clearActiveRequest(chainKey, entryId);
          return;
        }
        dispatch({ type: SUMMARY_ENTRY_ACCEPTED, payload: { chainKey, entryId, summaryId } });
        dispatchEntrySuccess(dispatch, { chainKey, entryId, summaryId, summaryResponse: data });
        clearActiveRequest(chainKey, entryId);
        return;
      }

      // PENDING — BE issued a summaryId (SD-4 item 1); store it and start polling by identity.
      const summaryId = data?.summaryId;
      if (!summaryId) {
        dispatchEntryError(dispatch, {
          chainKey,
          entryId,
          summaryId: null,
          error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: 'Summary request did not return a summaryId.' },
        });
        clearActiveRequest(chainKey, entryId);
        return;
      }

      dispatch({ type: SUMMARY_ENTRY_ACCEPTED, payload: { chainKey, entryId, summaryId } });
      activeRequestByChain[chainKey] = {
        ...activeRequestByChain[chainKey],
        entryId,
        summaryId,
        pendingRequest: null,
        pollTimeoutId: null,
      };
      dispatch(pollSummaryStatus({ chainKey, entryId, summaryId, attempt: 1 }));
    } catch (error) {
      if (!isEntryStillActive(chainKey, entryId)) return; // cancelled — FR-1.9 already dispatched CANCELLED
      dispatchEntryError(dispatch, {
        chainKey,
        entryId,
        summaryId: null,
        error: { code: 'DOWNSTREAM_SUMMARIZATION_FAILED', message: error?.message },
      });
      clearActiveRequest(chainKey, entryId);
      Logger.error('Error while generating summary:', error);
    }
  };
}

/**
 * FR-1.9: cancel-during-generation. SD-4 item 5 — no server-side cancel in V1; this is exactly
 * MessageBroker's client-side abort plus poll teardown.
 */
export function cancelSummaryGeneration({ chainKey, context }) {
  return (dispatch) => {
    const active = activeRequestByChain[chainKey];
    if (!active) return;

    const { entryId, summaryId = null, pendingRequest, pollTimeoutId } = active;
    if (pollTimeoutId) clearTimeout(pollTimeoutId);
    pausedPollsByChain.delete(chainKey);
    if (!pausedPollsByChain.size) removeVisibilityChangeHandler();
    delete activeRequestByChain[chainKey];

    if (pendingRequest?.cancel) {
      pendingRequest.cancel();
    }

    dispatch({ type: SUMMARY_ENTRY_CANCELLED, payload: { chainKey, entryId, summaryId } });
    trackSummaryGenerationCancelled(context ?? active.context);
  };
}

const limitToReferencedResults =
  ({ requestUrl, requestIdentifiers, errorSubject, databaseId, context, sort, order, chainKey, entryId }) =>
  async (dispatch) => {
    try {
      const response = await MessageBroker.send(requestUrl, { databaseId, context, ...requestIdentifiers });
      const searchId = await searchStorage.setNew({ searchType: SearchTypes.SUMMARY_REFERENCES });
      const { resultSize, resultName: destinationResultName } = response;
      const searchParams = {
        ...response,
        context,
        ...(sort === undefined ? {} : { sort }),
        ...(order === undefined ? {} : { order }),
        searchId,
        resetBreadcrumbs: false,
      };

      await Breadcrumb.createBreadcrumb(searchParams, resultSize);

      // FR-8.8/SD-1: register the destination on the triggering entry's validForResultNames so
      // staleness treats it as a valid context (a references-only navigation), not a filter.
      if (chainKey && entryId && destinationResultName) {
        dispatch({
          type: SUMMARY_ENTRY_ADD_VALID_RESULT_NAME,
          payload: { chainKey, entryId, resultName: destinationResultName },
        });
      }

      ResultsDataManage.redirectToResults(searchParams);
    } catch (error) {
      Logger.error(`Error while limiting to referenced ${errorSubject}:`, error);
    }
  };

export const limitToReferencedDocuments = withGlobalSpinnerActionEnhancer(({ cnrList, ...requestParams }) =>
  limitToReferencedResults({
    ...requestParams,
    requestUrl: RequestUrls.aiSearchLimitToReferencedDocuments,
    requestIdentifiers: { cnrList },
    errorSubject: 'documents',
    context: citations,
  })
);

export const limitToReferencedReactions = withGlobalSpinnerActionEnhancer(({ reactionIds, ...requestParams }) =>
  limitToReferencedResults({
    ...requestParams,
    requestUrl: RequestUrls.aiSearchLimitToReferencedReactions,
    requestIdentifiers: { reactionIds },
    errorSubject: 'reactions',
    context: reactions,
  })
);

export const copySummaryToClipboard =
  ({ summary, referencedCitations, referencedReactions, isReactionSummary, context }) =>
  async () => {
    try {
      const isReaction = isReactionSummary ?? !!referencedReactions?.length;
      trackCopyToClipboard(context || (isReaction ? reactions : citations));
      const references = isReaction ? referencedReactions : referencedCitations;
      const summarySection = replaceRefsInSummaryWithIndexes(summary, references, { isReactionSummary: isReaction });
      const referencesSection = isReaction
        ? formatReferencesCopyToClipboardUtil(summary, referencedReactions, { type: 'reactions' })
        : formatReferencesCopyToClipboardUtil(summary, referencedCitations);

      await navigator.clipboard.writeText(`${summarySection}${referencesSection}`);

      newToastMessage({
        content: 'Summary copied successfully to clipboard.',
      });
    } catch (e) {
      Logger.error('Error while copying summary:', e);
    }
  };

// FR-4.2: feedback persists on the entry (SD-1), surviving view switches/stacking/navigation.
export function submitSummaryFeedback({ chainKey, entryId, liked, context }) {
  return (dispatch) => {
    dispatch({
      type: SUMMARY_ENTRY_FEEDBACK,
      payload: { chainKey, entryId, feedbackSubmitted: liked ? 'up' : 'down' },
    });
    trackSurveySubmissionForSummary(liked, context);
  };
}

// Explicit session-wide reset (e.g. logout) — NOT called on Assistant view unmount (FR-1.6).
export const resetSummaryState = () => ({ type: RESET_SUMMARY_STATE });
